@if (!isBatchScoped) {
  <div class="d-flex flex-wrap justify-content-between align-items-center mb-3">
    <div>
      <h5 class="mb-1 fw-bold">{{ containerLabel }} Stock Adjustments</h5>
      <span class="sub-text">
        Manual corrections to batch stock
        {{ isFactory ? 'in the factory go-down' : 'in the finished goods warehouses' }}
      </span>
    </div>
  </div>
}

@if (isForbidden) {
  <app-not-authorized
    [title]="'You do not have access to ' + containerLabel.toLowerCase() + ' stock adjustments'"
    message="Manual stock adjustments are restricted to administrators."></app-not-authorized>
} @else {
  @if (!isBatchScoped) {
    <div class="alert alert-info d-flex align-items-start mb-3" role="alert">
      <i class="bi bi-info-circle-fill me-2 mt-1"></i>
      <div>
        <strong>Adjustments are permanent.</strong>
        Nothing here can be edited or deleted. To undo one, post the opposite adjustment from the batch on the
        {{ isFactory ? 'Factory Stock' : 'Finished Goods' }} tab: both entries stay on the record. Every row shows the
        batch balance and the product's {{ isFactory ? 'factory' : 'warehouse' }} total immediately after the change.
      </div>
    </div>

    <!-- Filters: search and product, then container/direction/reason, then who and when.
         Each group sums to 12 columns so it takes a full row of its own. -->
    <div class="row g-2 mb-3">
      <div class="col-12 col-md-8 col-lg-8">
        <div class="input-group">
          <span class="input-group-text bg-white border-end-0">
            <i class="bi bi-search text-muted"></i>
          </span>
          <input
            type="text"
            class="form-control border-start-0 ps-0"
            placeholder="Search batch, production no., product or remarks"
            [ngModel]="searchTerm"
            (ngModelChange)="onSearchChange($event)" />
        </div>
      </div>

      <div class="col-12 col-md-4 col-lg-4">
        <ng-select
          placeholder="All Products"
          [items]="products"
          [(ngModel)]="selectedProduct"
          bindValue="id"
          bindLabel="name"
          [clearable]="true"
          [searchable]="true"
          (ngModelChange)="onFilterChange()" />
      </div>

      <div class="col-12 col-md-4 col-lg-4">
        <ng-select
          [placeholder]="isFactory ? 'All Factories' : 'All Warehouses'"
          [items]="containerOptions"
          [(ngModel)]="selectedContainer"
          bindValue="id"
          bindLabel="name"
          [clearable]="true"
          [searchable]="false"
          (ngModelChange)="onFilterChange()" />
      </div>

      <div class="col-12 col-md-4 col-lg-4">
        <ng-select
          placeholder="Added or removed"
          [items]="directionOptions"
          [(ngModel)]="selectedDirection"
          bindValue="value"
          bindLabel="label"
          [clearable]="true"
          [searchable]="false"
          (ngModelChange)="onFilterChange()" />
      </div>

      <div class="col-12 col-md-4 col-lg-4">
        <ng-select
          placeholder="All Reasons"
          [items]="reasonOptions"
          [(ngModel)]="selectedReason"
          bindValue="value"
          bindLabel="label"
          [clearable]="true"
          [searchable]="false"
          (ngModelChange)="onFilterChange()" />
      </div>

      <div class="col-12 col-md-4 col-lg-4">
        <ng-select
          placeholder="Adjusted by anyone"
          [items]="employees"
          [(ngModel)]="selectedCreatedBy"
          bindValue="user_id"
          bindLabel="full_name"
          [clearable]="true"
          [searchable]="true"
          (ngModelChange)="onFilterChange()" />
      </div>

      <div class="col-6 col-md-4 col-lg-4">
        <input
          type="date"
          class="form-control"
          aria-label="From date"
          [(ngModel)]="fromDate"
          (ngModelChange)="onFilterChange()" />
      </div>

      <div class="col-6 col-md-4 col-lg-4">
        <input
          type="date"
          class="form-control"
          aria-label="To date"
          [(ngModel)]="toDate"
          (ngModelChange)="onFilterChange()" />
      </div>

      @if (hasActiveFilters) {
        <div class="col-12 col-md-4 col-lg-3 d-flex">
          <button
            class="btn btn-secondary btn-sm w-100 d-flex align-items-center justify-content-center gap-1"
            (click)="clearFilters()">
            <i class="bi bi-x-circle"></i>
            Clear filters
          </button>
        </div>
      }
    </div>
  }

  <app-error-alert [error]="pageError"></app-error-alert>

  @if (isLoading) {
    <app-loading-spinner></app-loading-spinner>
  } @else if (adjustments.length === 0) {
    @if (isBatchScoped) {
      <app-info-alert>This batch has no stock adjustments.</app-info-alert>
    } @else {
      <div class="empty-state-container">
        <i class="bi bi-sliders"></i>
        <h5>No {{ containerLabel }} Stock Adjustments</h5>
        @if (hasActiveFilters) {
          <p class="mb-0">No adjustments match the current search or filters.</p>
        } @else {
          <p class="mb-0">
            Adjustments are made from the
            <strong>{{ isFactory ? 'Factory Stock' : 'Finished Goods' }}</strong>
            tab, using the Adjust action on a batch row.
          </p>
        }
      </div>
    }
  } @else {
    <div class="table-responsive border bg-white">
      <table class="table align-middle mb-0">
        <thead class="table-light">
          <tr>
            <th>Date</th>
            @if (!isBatchScoped) {
              <th>Batch</th>
              <th>Product</th>
              <th>{{ containerLabel }}</th>
            }
            <th class="text-end">Change</th>
            <th class="text-end">Batch After</th>
            <th class="text-end">{{ containerLabel }} After</th>
            <th>Reason</th>
            <th>Remarks</th>
            <th>By</th>
          </tr>
        </thead>
        <tbody>
          @for (adjustment of adjustments; track adjustment.id) {
            <tr>
              <td class="text-nowrap">
                <div>{{ adjustment.created_at | date: 'dd MMM yyyy' }}</div>
                <div class="text-muted small">{{ adjustment.created_at | date: 'shortTime' }}</div>
              </td>
              @if (!isBatchScoped) {
                <td>
                  <div class="fw-semibold font-monospace">{{ adjustment.batch_number }}</div>
                  <div class="text-muted small font-monospace">{{ adjustment.production_number }}</div>
                </td>
                <td>
                  <div>{{ adjustment.product_name }}</div>
                  @if (adjustment.packaging_type) {
                    <div class="text-muted small">{{ adjustment.packaging_type }}</div>
                  }
                </td>
                <td class="text-nowrap">{{ adjustment.container_name || '—' }}</td>
              }
              <td class="text-end text-nowrap">
                <span
                  class="fw-bold"
                  [class.text-danger]="isRemoval(adjustment)"
                  [class.text-success]="!isRemoval(adjustment)">
                  {{ signedQuantity(adjustment) }}
                </span>
                @if (boxesLabel(adjustment)) {
                  <div class="text-muted small font-monospace">({{ boxesLabel(adjustment) }})</div>
                }
              </td>
              <td class="text-end text-nowrap">{{ batchBalanceAfter(adjustment) }}</td>
              <td class="text-end text-nowrap">{{ containerBalanceAfter(adjustment) }}</td>
              <td>{{ reasonLabel(adjustment) }}</td>
              <td class="remarks-cell">
                @if (adjustment.remarks) {
                  {{ adjustment.remarks }}
                } @else {
                  <span class="text-muted">&mdash;</span>
                }
              </td>
              <td class="text-nowrap">{{ adjustment.created_by_name }}</td>
            </tr>
          }
        </tbody>
      </table>
    </div>

    <div class="mt-4">
      <app-pagination
        [currentPage]="currentPage"
        [totalCount]="totalCount"
        [pageSize]="pageSize"
        [hasNextPage]="hasNextPage"
        [hasPreviousPage]="hasPreviousPage"
        (pageChange)="onPageChange($event)"></app-pagination>
    </div>
  }
}
